import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Plus, Pencil, Trash2, X, Save } from 'lucide-react'
import { faqsApi } from '../../lib/api'
import { Card } from '../ui/Card'
import { Button } from '../ui/Button'
import type { FAQItem } from '../../types'

export function FaqsEditor() {
  const { t } = useTranslation()
  const [faqs, setFaqs] = useState<FAQItem[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [editingId, setEditingId] = useState<string | null>(null)

  const schema = z.object({
    question: z.string().min(5, t('admin.faqs.errors.question')),
    answer: z.string().min(10, t('admin.faqs.errors.answer')),
  })

  type FormData = z.infer<typeof schema>

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<FormData>({
    resolver: zodResolver(schema),
  })

  const loadFaqs = useCallback(async () => {
    setIsLoading(true)
    setError(null)
    try {
      const data = await faqsApi.getAll()
      setFaqs(data)
    } catch (err) {
      setError(err instanceof Error ? err.message : t('admin.faqs.error'))
    } finally {
      setIsLoading(false)
    }
  }, [t])

  useEffect(() => {
    let cancelled = false
    faqsApi
      .getAll()
      .then((data) => {
        if (!cancelled) setFaqs(data)
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error ? err.message : t('admin.faqs.error'))
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [t])

  const handleEdit = (faq: FAQItem) => {
    setEditingId(faq.id)
    reset({ question: faq.question, answer: faq.answer })
  }

  const handleCancel = () => {
    setEditingId(null)
    reset({ question: '', answer: '' })
  }

  const onSubmit = async (data: FormData) => {
    try {
      if (editingId) {
        await faqsApi.update(editingId, data)
      } else {
        await faqsApi.create(data)
      }
      await loadFaqs()
      handleCancel()
    } catch (err) {
      setError(err instanceof Error ? err.message : t('admin.faqs.error'))
    }
  }

  const handleDelete = async (id: string) => {
    if (!window.confirm(t('admin.faqs.confirmDelete'))) return
    try {
      await faqsApi.delete(id)
      await loadFaqs()
    } catch (err) {
      setError(err instanceof Error ? err.message : t('admin.faqs.error'))
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold text-text-primary">{t('admin.faqs.title')}</h1>
        <Button
          size="sm"
          onClick={() => {
            setEditingId('new')
            reset({ question: '', answer: '' })
          }}
        >
          <Plus className="mr-2 h-4 w-4" />
          {t('admin.faqs.add')}
        </Button>
      </div>

      {editingId && (
        <Card variant="gradient">
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <div>
              <label className="mb-2 block text-sm font-medium text-text-primary">
                {t('admin.faqs.form.question')}
              </label>
              <input
                {...register('question')}
                className="w-full rounded-lg border border-border-subtle bg-bg px-4 py-3 text-text-primary focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
              />
              {errors.question && <p className="mt-1 text-sm text-error">{errors.question.message}</p>}
            </div>
            <div>
              <label className="mb-2 block text-sm font-medium text-text-primary">
                {t('admin.faqs.form.answer')}
              </label>
              <textarea
                {...register('answer')}
                rows={4}
                className="w-full rounded-lg border border-border-subtle bg-bg px-4 py-3 text-text-primary focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent resize-none"
              />
              {errors.answer && <p className="mt-1 text-sm text-error">{errors.answer.message}</p>}
            </div>
            <div className="flex gap-3">
              <Button type="submit" isLoading={isSubmitting}>
                <Save className="mr-2 h-4 w-4" />
                {editingId === 'new' ? t('admin.faqs.create') : t('admin.faqs.update')}
              </Button>
              <Button type="button" variant="secondary" onClick={handleCancel}>
                <X className="mr-2 h-4 w-4" />
                {t('admin.faqs.cancel')}
              </Button>
            </div>
          </form>
        </Card>
      )}

      {error && (
        <Card variant="default" className="border-error/30">
          <p className="text-error">{error}</p>
        </Card>
      )}

      {isLoading ? (
        <div className="flex h-64 items-center justify-center">
          <div className="h-8 w-8 animate-spin rounded-full border-2 border-accent border-t-transparent" />
        </div>
      ) : faqs.length === 0 ? (
        <Card variant="default" className="text-center">
          <p className="text-text-secondary">{t('admin.faqs.empty')}</p>
        </Card>
      ) : (
        <div className="space-y-3">
          {faqs.map((faq) => (
            <Card key={faq.id} variant="default" hover={false}>
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <h3 className="font-medium text-text-primary">{faq.question}</h3>
                  <p className="mt-1 text-sm text-text-secondary">{faq.answer}</p>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => handleEdit(faq)}
                    className="rounded-lg p-2 text-text-secondary transition-colors hover:bg-surface-elevated hover:text-accent-light"
                    aria-label={t('admin.faqs.edit')}
                  >
                    <Pencil className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDelete(faq.id)}
                    className="rounded-lg p-2 text-text-secondary transition-colors hover:bg-error/10 hover:text-error"
                    aria-label={t('admin.faqs.delete')}
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  )
}
